import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormControl, IconButton, InputAdornment, Typography } from '@material-ui/core';
import {
  EditOutlined as EditIcon,
  EmailOutlined as EmailIcon,
  LocalHospital as HospitalIcon,
  LocationOn as LocationOnIcon,
  PeopleOutlined as PeopleIcon,
  PhoneOutlined as PhoneIcon,
} from '@material-ui/icons';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ErrorIconOutlined from '@material-ui/icons/ErrorOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import clsx from 'clsx';
import { Button, CheckBox, Input } from 'components/atoms';
import SearchableSelect from 'components/mocules/SearchableSelect';
import { BRAND_NAME, ENUM_SCOPE } from 'constants/Enums';
import useLeadSources from 'hooks/useLeadSources';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { Controller, useForm } from 'react-hook-form';
import AddressService from 'services/AddressService';
import { ENV_COUNTRY, LANG_DEFAULT } from 'sysconfig';
import { NotifyUtils, ValidateUtils, regUtils } from 'utils';
import { getRegisterURL } from 'utils/registerURL';
import * as yup from 'yup';
import useMobileV2 from 'zustand-lib/storeMobile';
import styles from './styles.module.css';

const { validateData, Error, isEmpty } = ValidateUtils;

// update to zustand for country
const { PHONE_REGEX, PHONE_REGEX_CAM, EMAIL_REGEX } = regUtils;

// Lead source option whose selection requires the free-text `otherSourceDetail`
const LEAD_SOURCE_OTHER = 'other';

// Business type (scope) options, same order as the former 2-column radio list
const SCOPE_OPTIONS = [
  ENUM_SCOPE.PHARMACY,
  ENUM_SCOPE.PHARMA_COMPANY,
  ENUM_SCOPE.CLINIC,
  ENUM_SCOPE.HOSPITAL,
  ENUM_SCOPE.DENTISTRY,
  ENUM_SCOPE.BEAUTY_SALON,
  ENUM_SCOPE.HEALTH_CENTER,
  ENUM_SCOPE.CABINET,
];

const validateSignUp = (
  { isCheckAgree, name, email, password, phone },
  failCallback,
  translatePopupAction,
) => {
  try {
    validateData.name(name);
    validateData.phoneNumber(phone);
    if (!isEmpty(email)) {
      validateData.email(email);
    }
    validateData.password(password);
    if (!isCheckAgree) throw new Error('message_agree_terms');
    return true;
  } catch (error) {
    NotifyUtils.error(
      translatePopupAction(`popup_register.${error?.message}`) ||
        translatePopupAction('popup_register.message_error'),
    );
    failCallback(error);
    return false;
  }
};

const NewSignUpForm = React.memo((props) => {
  const { t: translatePopupAction, i18n } = useTranslation('popup-action');
  const { t: translateCommon } = useTranslation('common');
  const [showPassword, setShowPassword] = useState(false);
  const { className, onClickSignIn, onClickSignUp, referCode, page, setPage, handleScrollTop } =
    props;
  const [provinces, setProvinces] = useState([]);
  const beta = useMobileV2((state) => state.beta);
  const router = useRouter();

  // Lead source: only shown and required when the API returns a non-empty list.
  // While loading / on error / empty list the field is hidden and never blocks registration.
  const { leadSources } = useLeadSources();
  const hasLeadSources = leadSources.length > 0;
  const currentLocale = i18n?.language || router?.locale || LANG_DEFAULT;
  const leadSourceOptions = useMemo(
    () =>
      leadSources.map((source) => ({
        value: source.value,
        label: source.labels?.[currentLocale] || source.label,
      })),
    [leadSources, currentLocale],
  );
  const scopeOptions = useMemo(
    () => SCOPE_OPTIONS.map((item) => ({ value: item, label: translateCommon(`scope.${item}`) })),
    [translateCommon],
  );

  const leadSourceSchema = hasLeadSources
    ? {
        leadSource: yup
          .string()
          .required(translatePopupAction('popup_register.lead_source_required')),
        otherSourceDetail: yup
          .string()
          .trim()
          .when('leadSource', {
            is: LEAD_SOURCE_OTHER,
            then: (fieldSchema) =>
              fieldSchema.required(
                translatePopupAction('popup_register.other_source_detail_required'),
              ),
          }),
      }
    : {};

  const schema = yup
    .object({
      name: yup
        .string()
        .required(translatePopupAction('popup_register.validate_require_username'))
        .min(2, translatePopupAction('popup_register.validate_username_min_2_word')),
      password: yup
        .string()
        .required(translatePopupAction('popup_register.validate_password_empty')),
      phone: yup
        .string()
        .required(translatePopupAction('popup_register.not_entered_your_phoneNumber'))
        .matches(
          ENV_COUNTRY === 'vn' ? PHONE_REGEX : PHONE_REGEX_CAM,
          translatePopupAction('popup_register.phone_not_correct_format'),
        ),
      email: yup
        .string()
        .matches(EMAIL_REGEX, translatePopupAction('popup_register.email_not_correct_format')),
      ...leadSourceSchema,
    })
    .required();
  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
    watch,
  } = useForm({ resolver: yupResolver(schema) });
  const selectedLeadSource = watch('leadSource');

  useEffect(() => {
    const getListProvince = async () => {
      try {
        const provinceResult = await AddressService.getProvinces();
        setProvinces(provinceResult);
        reset({ provinceCode: provinceResult[0]?.value });
      } catch (error) {
        // NotifyUtils.error('Lấy danh sách tỉnh thành không thành công');
      }
    };
    getListProvince();
  }, [reset]);

  const handleSubmitSignUp = useCallback(
    (data) => {
      // validate
      if (
        validateSignUp(
          data,
          (error) => {
            if (error) {
              const newError = {};
              newError[error.type] = true;
              // setErrors({ ...newError });
            }
          },
          translatePopupAction,
        )
      ) {
        // Lead tracking: registerURL is always sent (backend reads UTM from it, last-click UTM landing URL);
        // leadSource / otherSourceDetail only when the field is shown and selected.
        const { leadSource, otherSourceDetail, ...restData } = data;
        const signUpData = { ...restData, registerURL: getRegisterURL() };
        if (hasLeadSources && leadSource) {
          signUpData.leadSource = leadSource;
          if (leadSource === LEAD_SOURCE_OTHER) {
            signUpData.otherSourceDetail = otherSourceDetail;
          }
        }
        onClickSignUp(signUpData);
      }
    },
    [onClickSignUp, hasLeadSources],
  );

  const handleClickSignIn = useCallback(
    (e) => {
      e.preventDefault();
      onClickSignIn();
    },
    [onClickSignIn],
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickTerms = useCallback(
    (e) => {
      e.preventDefault();
      router.push('/?terms=true');
      setPage(page + 2);
      handleScrollTop();
    },
    [page],
  );

  const IconAccount = (
    <InputAdornment position="start" className={styles.input_icon}>
      <AccountCircleOutlinedIcon />
    </InputAdornment>
  );
  const IconPassword = (
    <InputAdornment position="start" className={styles.input_icon}>
      <LockOutlinedIcon />
    </InputAdornment>
  );

  const IconPhone = (
    <InputAdornment position="start" className={styles.input_icon}>
      <PhoneIcon />
    </InputAdornment>
  );

  const IconEndPassword = (
    <InputAdornment position="end" className={styles.input_icon}>
      <IconButton onClick={handleClickShowPassword}>
        {!showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );
  const IconInviter = (
    <InputAdornment position="start" className={styles.input_icon}>
      <PeopleIcon />
    </InputAdornment>
  );
  const IconEmail = (
    <InputAdornment position="start" className={styles.input_icon}>
      <EmailIcon />
    </InputAdornment>
  );
  const IconOtherSource = (
    <InputAdornment position="start" className={styles.input_icon}>
      <EditIcon />
    </InputAdornment>
  );

  const labelAgree = (
    <div>
      {translatePopupAction('popup_register.agree_policy')}&nbsp;
      {beta && isMobile ? (
        <Box component="a" onClick={handleClickTerms} style={{ color: '#005c29' }}>
          {translatePopupAction('popup_register.policy')}
        </Box>
      ) : (
        <a
          href="/terms-and-condition"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#005c29' }}
        >
          {translatePopupAction('popup_register.policy')}
        </a>
      )}
      {/* <a href="/terms-and-condition" target="_blank" rel="noreferrer" style={{ color: '#005c29' }}>
        Điều khoản sử dụng*
      </a> */}
    </div>
  );

  return (
    <div className={clsx(className, styles.form_sign_up)}>
      {beta && isMobile && (
        <div className={styles.wrapperTitle} style={{ marginTop: '60px' }}>
          <p className="title">{translatePopupAction('popup_register.title')}</p>
        </div>
      )}
      <form className={className} onSubmit={handleSubmit(handleSubmitSignUp)}>
        <Controller
          name="name"
          defaultValue=""
          control={control}
          className={styles.form_control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Input
                startAdornment={IconAccount}
                placeholder={translatePopupAction('popup_register.username')}
                variant="outlined"
                error={!!errors.name || false}
                className={styles.input}
                data-test="signup-username"
                {...field}
              />
              {errors.name?.message && (
                <div className={styles.text_error} data-test="signup-err-username">
                  <ErrorIconOutlined /> {errors.name?.message}
                </div>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="phone"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Input
                startAdornment={IconPhone}
                placeholder={translatePopupAction('popup_register.phone_number')}
                variant="outlined"
                error={!!errors.phone || false}
                className={styles.input}
                data-test="signup-phone"
                {...field}
              />
              {errors.phone?.message && (
                <div className={styles.text_error} data-test="signup-err-phone">
                  <ErrorIconOutlined /> {errors.phone?.message}
                </div>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="email"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Input
                startAdornment={IconEmail}
                placeholder={translatePopupAction('popup_register.email')}
                variant="outlined"
                error={!!errors.email || false}
                className={styles.input}
                data-test="signup-email"
                {...field}
              />
              {errors.email?.message && (
                <div className={styles.text_error} data-test="signup-err-email">
                  <ErrorIconOutlined /> {errors.email?.message}
                </div>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="password"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="password"
                startAdornment={IconPassword}
                endAdornment={IconEndPassword}
                placeholder={translatePopupAction('popup_register.password')}
                variant="outlined"
                error={!!errors.password || false}
                className={styles.input}
                data-test="signup-password"
                {...field}
              />
              {errors.password?.message && (
                <div className={styles.text_error} data-test="signup-err-password">
                  <ErrorIconOutlined /> {errors.password?.message}
                </div>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="provinceCode"
          defaultValue={provinces[0]?.value}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <SearchableSelect
                ref={field.ref}
                id="signup-provinceCode"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={provinces}
                startIcon={<LocationOnIcon />}
                placeholder={translatePopupAction('popup_register.choose_province')}
                searchPlaceholder={translatePopupAction('popup_register.search_province')}
                noResultsText={translatePopupAction('popup_register.no_results')}
                className={styles.select_field}
                data-test="signup-provinceCode"
              />
            </FormControl>
          )}
        />
        {/* todo: */}
        <Controller
          name="referCode"
          defaultValue={referCode}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Input
                startAdornment={IconInviter}
                value={referCode}
                placeholder={referCode || translatePopupAction('popup_register.referral_number')}
                variant="outlined"
                // disabled={!!referCode}
                className={styles.input}
                data-test="signup-referCode"
                {...field}
              />
            </FormControl>
          )}
        />
        {hasLeadSources && (
          <Controller
            name="leadSource"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <SearchableSelect
                  ref={field.ref}
                  id="signup-leadSource"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={leadSourceOptions}
                  startIcon={<CampaignOutlinedIcon />}
                  placeholder={`${translatePopupAction('popup_register.lead_source_placeholder', {
                    brandname: BRAND_NAME,
                  })} *`}
                  searchPlaceholder={translatePopupAction('popup_register.search_lead_source')}
                  noResultsText={translatePopupAction('popup_register.no_results')}
                  error={!!errors.leadSource}
                  className={styles.select_field}
                  data-test="signup-leadSource"
                />
                {errors.leadSource?.message && (
                  <div className={styles.text_error} data-test="signup-err-leadSource">
                    <ErrorIconOutlined /> {errors.leadSource?.message}
                  </div>
                )}
              </FormControl>
            )}
          />
        )}
        {hasLeadSources && selectedLeadSource === LEAD_SOURCE_OTHER && (
          <Controller
            name="otherSourceDetail"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <Input
                  startAdornment={IconOtherSource}
                  placeholder={translatePopupAction(
                    'popup_register.other_source_detail_placeholder',
                  )}
                  variant="outlined"
                  error={!!errors.otherSourceDetail || false}
                  className={styles.input}
                  data-test="signup-otherSourceDetail"
                  {...field}
                  value={field.value || ''}
                />
                {errors.otherSourceDetail?.message && (
                  <div className={styles.text_error} data-test="signup-err-otherSourceDetail">
                    <ErrorIconOutlined /> {errors.otherSourceDetail?.message}
                  </div>
                )}
              </FormControl>
            )}
          />
        )}
        <Controller
          name="scope"
          defaultValue={ENUM_SCOPE.PHARMACY}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth className={styles.scope_field}>
              <SearchableSelect
                ref={field.ref}
                id="signup-scope"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={scopeOptions}
                startIcon={<HospitalIcon />}
                placeholder={translatePopupAction('popup_register.choose_your_business_type')}
                searchPlaceholder={translatePopupAction('popup_register.search_business_type')}
                noResultsText={translatePopupAction('popup_register.no_results')}
                className={styles.select_field}
                data-test="signup-scope"
              />
            </FormControl>
          )}
        />
        <Typography className={styles.disclaimer}>
          {translatePopupAction('popup_register.message_confirm_signup_account').replace(
            '{BRAND_NAME}',
            BRAND_NAME,
          )}
        </Typography>
        <Controller
          name="isCheckAgree"
          defaultValue={false}
          control={control}
          render={({ field }) => (
            <div className={styles.agree_term}>
              <CheckBox data-test="signup-isCheckAgree" label={labelAgree} {...field} />
            </div>
          )}
        />
        {isDesktop && (
          <div className={`${styles.link_login_wrapper} ${styles.center}`}>
            <p>
              {translatePopupAction('popup_register.have_account')}&nbsp;
              <a href="#top" className={styles.link_login} onClick={handleClickSignIn}>
                {translatePopupAction('popup_register.login')}
              </a>
            </p>
          </div>
        )}

        <Box className={styles.center}>
          <Button className={styles.btn_register} type="submit" data-test="signup-submit">
            {translatePopupAction('popup_register.create_account')}
          </Button>
        </Box>
      </form>
    </div>
  );
});

export default NewSignUpForm;
